import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Box, Button, Typography, Stack, IconButton } from '@mui/material';
import EditSquareIcon from '@mui/icons-material/EditSquare';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import GavelIcon from '@mui/icons-material/Gavel';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ListFiltersWidget from '../../components/widgets/list-filters.widget';
import { adminAuctionAuctionApi } from '../../apis/admin';
import {
  getApiErrorMessage,
  getFormattedDate,
  getFormattedPrice,
  getFormattedQuantity,
  parseIntoNumber,
  parseQueryStringToFilters,
} from '../../utilities/helper';
import { showSnackbar } from '../../store/slices/common.slice';
import {
  APIResponse,
  Auction,
  FilterConfig,
  ListingResponse,
  Pagination,
} from '../../utilities/types';
import AdminGridWidget from '../../components/widgets/admin-grid.widget';
import FormModal from '../../components/modals/form.modal';
import CustomSwal from '../../components/elements/custom-swal.element';
import AuctionForm from '../../components/forms/auction.form';
import useUserHook from '../../hooks/user.hook';
import AuctionBidGridWidget from '../../components/widgets/my-account/auction-bid-grid.widget';
import BidRequestGridWidget from '../../components/widgets/admin/bid-request-grid.widget';
import StatusChip from '../../components/elements/status_chip.element';
import { deleteAuction, updateAuction } from '../../apis';

const AdminAuctionsPage: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [page, setPage] = useState<number>(1);
  const [filterValues, setFilterValues] = useState<{ [key: string]: any }>({});
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [showAuctionForm, setShowAuctionForm] = useState<boolean>(false);
  const [showAuctionBidList, setShowAuctionBidList] = useState<boolean>(false);
  const [selectedAuction, setSelectedAuction] = useState<Auction | null>(null);
  const [showBidRequestList, setShowBidRequestList] = useState<boolean>(false);
  const [deleteIsInProgress, setDeleteIsInProgress] = useState<boolean>(false);

  const fetch = useCallback(async () => {
    try {
      const payload = {
        ...filterValues,
        // listing_type: 'current',
        page,
      };
      setLoadingData(true);
      const response = (await adminAuctionAuctionApi(payload)) as APIResponse<ListingResponse>;
      setAuctions(response.data.items);
      setPagination(response.data.pagination);
    } catch (err: any) {
      dispatch(showSnackbar({ message: getApiErrorMessage(err), severity: 'error' }));
    }
    setLoadingData(false);
  }, [filterValues, dispatch, page]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    const parsed = parseQueryStringToFilters(location.search);
    const initial: Record<string, any> = {};
    Object.entries(parsed).forEach((v) => {
      const [key, value] = v;
      initial[key] = value;
    });
    setFilterValues(initial);
  }, [location.search]);

  const callDeleteAuction = useCallback(
    async (id: number) => {
      try {
        setDeleteIsInProgress(true);
        const response = await deleteAuction({ id });
        if (response.status) {
          if (page > 1) {
            setPage(1);
          } else {
            fetch();
          }
          CustomSwal.fire({
            icon: 'success',
            title: 'User deleted successfully',
          });
        } else {
          throw new Error(response.message || 'Error in deleting user');
        }
        setDeleteIsInProgress(false);
      } catch (err) {
        dispatch(showSnackbar({ message: getApiErrorMessage(err), severity: 'error' }));
      }
    },
    [dispatch, fetch, page]
  );

  const askDeleteConfirmation = useCallback(
    (id: number) => {
      CustomSwal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
      }).then((result) => {
        if (result.isConfirmed) {
          callDeleteAuction(id);
        }
      });
    },
    [callDeleteAuction]
  );

  const toggleAuctionApproved = useCallback(async (auctionObject: Auction) => {
    const payload = { ...auctionObject, is_admin_approved: !auctionObject.is_admin_approved};
    updateAuction(payload).then((response) => {
      if (!response.status) {
        throw new Error('Error while toggling status');
      }
      setAuctions(auctions.map(item => {
        return {...item, is_admin_approved: item.id === auctionObject.id ? !auctionObject.is_admin_approved : item.is_admin_approved};
      }));
    }).catch(err => {
      dispatch(showSnackbar({ message: getApiErrorMessage(err), severity: 'error' }));
    });
  }, [auctions, dispatch]);

  const getRowCTA = (row: Auction) => {
    return (
      <Stack flexDirection="row" gap={0.5}>
        <IconButton
          // variant="contained"
          size="small"
          color="secondary"
          title="Click to view bids"
          onClick={() => {
            setSelectedAuction(row);
            setShowAuctionBidList(true);
          }}
          sx={{ minWidth: 'auto' }}
        >
          <GavelIcon />
        </IconButton>
        <IconButton
          // variant="contained"
          size="small"
          color="warning"
          title="Click to view bid requests"
          onClick={() => {
            setSelectedAuction(row);
            setShowBidRequestList(true);
          }}
          sx={{ minWidth: 'auto' }}
        >
          <AssignmentTurnedInIcon />
        </IconButton>
        <IconButton
          // variant="contained"
          size="small"
          color="info"
          title="Click to edit auction"
          onClick={() => {
            setShowAuctionForm(true);
            setSelectedAuction(row);
          }}
          sx={{ minWidth: 'auto' }}
        >
          <EditSquareIcon />
        </IconButton>
        <IconButton
          // variant="contained"
          size="small"
          color="error"
          title="Click to delete auction"
          onClick={() => {
            askDeleteConfirmation(Number(row.id));
          }}
          disabled={deleteIsInProgress}
          sx={{ minWidth: 'auto' }}
        >
          <DeleteIcon />
        </IconButton>
      </Stack>
    );
  };

  const { users } = useUserHook('seller');

  const columns = [
    { field: 'custom_id', headerName: 'ID', width: 70 },
    { field: 'title', headerName: 'Title', width: 150 },
    {
      field: 'item_name',
      headerName: 'Item Name',
      width: 150,
      renderCell: (params: any) => {
        return params.row?.item?.name || '--';
      },
    },
    {
      field: 'price',
      headerName: 'Pricing',
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Stack>
            <Typography variant="body2">
              <strong>Starting Price: </strong>
              {getFormattedPrice(params.row.starting_price)}
            </Typography>
            <Typography variant="body2">
              <strong>Current Price: </strong>
              {getFormattedPrice(params.row.current_price)}
            </Typography>
          </Stack>
        );
      },
    },
    // { field: 'description', headerName: 'Description', flex: 1.5 },
    {
      field: 'quantity',
      headerName: 'Quantity',
      width: 120,
      renderCell: (params: any) => {
        return getFormattedQuantity(params.value, params.row.quantity_unit);
      },
    },
    {
      field: 'total_bids',
      headerName: 'Total Bids',
      width: 120,
    },
    {
      field: 'end_time',
      headerName: 'Start-End Time',
      flex: 1,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <Stack>
            <Typography variant="body2">
              <strong>Start Time: </strong>
              {getFormattedDate(params.row.start_time)}
            </Typography>
            <Typography variant="body2">
              <strong>End Time: </strong>
              {getFormattedDate(params.row.end_time)}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'is_admin_approved',
      headerName: 'Approved',
      flex: 1,
      sortable: false,
      renderCell: (params: any) => {
        return <StatusChip value={params.row.is_admin_approved} onClick={() => toggleAuctionApproved(params.row)}/>
      },
    },
    // {
    //   field: 'image',
    //   headerName: 'Image',
    //   width: 100,
    //   cellClassName: 'item-img-cell',
    //   renderCell: (params: any) => (
    //     <img
    //       alt="item"
    //       src={prepareAndGetMediaURL(params.row?.item?.image)}
    //       style={{ maxWidth: 60, height: 'auto', maxHeight: 60, objectFit: 'cover' }}
    //     />
    //   ),
    // },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 170,
      sortable: false,
      renderCell: (params: any) => getRowCTA(params.row),
    },
  ];

  const filterConfig: FilterConfig[] = [
    {
      name: 'seller_id',
      label: 'Seller',
      type: 'select',
      options: users.map((u) => ({ value: u.id, label: u.name })),
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
    },
    {
      name: 'listing_type',
      label: 'Running Status',
      type: 'select',
      options: [
        { value: 'current', label: 'Currently Running' },
        { value: 'past', label: 'Expired' },
        { value: 'future', label: 'Upcoming' },
        { value: 'active', label: 'Current & Upcoming' },
      ],
    },
    {
      name: 'is_admin_approved',
      label: 'Admin Approved',
      type: 'select',
      options: [
        { value: 0, label: 'No' },
        { value: 1, label: 'Yes' },
      ],
    },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Auctions</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          variant="contained"
          color="info"
          onClick={() => {
            setShowAuctionForm(true);
          }}
        >
          Add New Auction
        </Button>
      </Box>
      <Box sx={{ mb: 2 }}>
        <ListFiltersWidget
          filters={filterConfig}
          onSearch={setFilterValues}
          onReset={() => setFilterValues({})}
        />
      </Box>
      <AdminGridWidget
        columns={columns}
        rows={auctions}
        page={page}
        onPageChange={setPage}
        loading={loadingData}
        totalRows={pagination ? pagination.total : 0}
      />
      {showAuctionForm && (
        <FormModal
          open={showAuctionForm}
          onClose={() => {
            setShowAuctionForm(false);
            setSelectedAuction(null);
          }}
          size="sm"
          // title={selectedAuction?.id ? 'Edit Seller' : 'Add New Seller'}
        >
          <AuctionForm
            auction_id={Number(selectedAuction?.id)}
            onSuccess={() => {
              setShowAuctionForm(false);
              if (page > 1) {
                setPage(1);
              } else {
                fetch();
              }
              CustomSwal.fire({
                icon: 'success',
                title: 'Auction saved successfully',
              });
            }}
          />
        </FormModal>
      )}
      {selectedAuction && (
        <>
          {showAuctionBidList && (
            <FormModal
              open={showAuctionBidList}
              onClose={() => {
                setShowAuctionBidList(false);
                setSelectedAuction(null);
              }}
              size="lg"
              title="Auction Bids"
            >
              <AuctionBidGridWidget auction_id={parseIntoNumber(selectedAuction.id)} />
            </FormModal>
          )}
          {showBidRequestList && (
            <FormModal
              open={showBidRequestList}
              onClose={() => {
                setShowBidRequestList(false);
                setSelectedAuction(null);
              }}
              size="md"
              title="Auction Bids Requests"
            >
              <BidRequestGridWidget auction_id={parseIntoNumber(selectedAuction.id)} />
            </FormModal>
          )}
        </>
      )}
    </>
  );
};

export default AdminAuctionsPage;
